/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/10/22
 */

import React from "react";
import {
	Button, Checkbox,
	Dialog, DialogActions,
	DialogContent,
	DialogTitle,
	FormControl, FormControlLabel, FormHelperText, InputLabel, Link, MenuItem, Select,
	Stack, TextField
} from "@mui/material";
import {Employee} from "../interfaces/Global";
import {LoadingButton} from "@mui/lab";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {addHours, addMinutes, startOfToday} from "date-fns";
import {EmployeeTagsQuery} from "../graphql/queries/employees";
import TypeaheadControl from "../controls/TypeaheadControl";
import {useMutation} from "urql";
import {UpdateEmployeeMutation, UpdateEmployeeResponse, UpdateEmployeeVars} from "../graphql/mutations/employees";
import CapacityModifierControl from "../controls/CapacityModifierControl";
import { toast } from "react-toastify";

export interface EmployeeSettingsDialogProps {
	employee: Employee
	open: boolean
	onClose: () => void
}

export const convertStringTimeToDate = (time: string) => {
	const [sH, sM] = time.split(":").map(i => Number(i));
	const now = startOfToday();
	return addMinutes(addHours(now, sH), sM);
}

export const convertTimeToString = (time: Date) => {
	const h = time.getHours(), m = time.getMinutes();
	return h + ":" + (m < 10 ? "0" + m : m);
}

export default function EmployeeSettingsDialog({ open, onClose, employee }: EmployeeSettingsDialogProps) {

	const [startTime, setStartTime] = React.useState(convertStringTimeToDate(employee.startTime));
	const [endTime, setEndTime] = React.useState(convertStringTimeToDate(employee.endTime));
	const [dobEnabled, setDobEnabled] = React.useState(!!employee.dateOfBirth);
	const [dobDay, setDobDay] = React.useState(employee.dateOfBirth?.getDate() || 1);
	const [dobMonth, setDobMonth] = React.useState(employee.dateOfBirth?.getMonth() || 0);
	const [tags, setTags] = React.useState(employee.tags || []);
	const [gsacOooEnabled, setGsacOooEnabled] = React.useState(employee.gsacOooEnabled);
	const [capacityModifier, setCapacityModifier] = React.useState(employee.capacityModifier);

	const [updateResult, updateEmployee] = useMutation<UpdateEmployeeResponse, UpdateEmployeeVars>(UpdateEmployeeMutation);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth={"xs"}
		>
			<DialogTitle>Settings - {employee.displayName}</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<Stack>
						<Stack direction={"row"} spacing={2}>
							<Stack flexGrow={1}>
								<TimePicker
									label="Start Time"
									value={startTime}
									onChange={(value: any) => {
										if (value) {
											setStartTime(value);
										}
									}}
									renderInput={(params: any) => <TextField variant={"standard"} {...params} />}
								/>
							</Stack>
							<Stack flexGrow={1}>
								<TimePicker
									label="End Time"
									value={endTime}
									onChange={(value: any) => {
										if (value) {
											setEndTime(value);
										}
									}}
									renderInput={(params: any) => <TextField variant={"standard"} {...params} />}
								/>
							</Stack>
						</Stack>
						<FormHelperText>Use local time for this employee.</FormHelperText>
					</Stack>
					<FormControl variant={"standard"} fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={dobEnabled}
									onChange={(event) => {
										setDobEnabled(event.target.checked);
									}}
								/>
							}
							label="Enable Date of Birth"
						/>
						<FormHelperText>If specified, date of birth will be highlighted in the calendar.</FormHelperText>
					</FormControl>
					{dobEnabled &&
					<Stack direction={"row"} spacing={2}>
						<Stack sx={{ width: 100 }}>
							<FormControl fullWidth variant={"standard"}>
								<InputLabel>Date Of Birth Day</InputLabel>
								<Select
									value={dobDay}
									onChange={(event) => {
										setDobDay(Number(event.target.value));
									}}
								>
									{[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31].map(d =>
										<MenuItem
											key={d}
											value={d}
											disabled={([1,3,5,8,10].includes(dobMonth) && d === 31) || (dobMonth === 1 && d > 28)}
										>
											{d}
										</MenuItem>
									)}
								</Select>
							</FormControl>
						</Stack>
						<Stack flexGrow={5}>
							<FormControl fullWidth variant={"standard"}>
								<InputLabel>Date Of Birth Month</InputLabel>
								<Select
									value={dobMonth}
									onChange={(event) => {
										const month = Number(event.target.value);
										setDobMonth(Number(month));
										if (([1,3,5,8,10].includes(month) && dobDay === 31) || (month === 1 && dobDay > 28)) {
											setDobDay(month === 1 ? 28 : 30);
										}
									}}
								>
									<MenuItem value={0}>January</MenuItem>
									<MenuItem value={1}>February</MenuItem>
									<MenuItem value={2}>March</MenuItem>
									<MenuItem value={3}>April</MenuItem>
									<MenuItem value={4}>May</MenuItem>
									<MenuItem value={5}>June</MenuItem>
									<MenuItem value={6}>July</MenuItem>
									<MenuItem value={7}>August</MenuItem>
									<MenuItem value={8}>September</MenuItem>
									<MenuItem value={9}>October</MenuItem>
									<MenuItem value={10}>November</MenuItem>
									<MenuItem value={11}>December</MenuItem>
								</Select>
							</FormControl>
						</Stack>
					</Stack>}
					<FormControl variant={"standard"} fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={gsacOooEnabled}
									onChange={(event) => {
										setGsacOooEnabled(event.target.checked);
									}}
								/>
							}
							label="Enable automatic labeling of GSAC tickets"
						/>
						<FormHelperText>
							When enabled, "ooo" label will be added to all GSAC tickets assigned to this employee when unavailable. <Link href={"https://hello.atlassian.net/wiki/spaces/BE/pages/744040509/Automatic+labeling+of+owned+tickets+as+OOO+for+employees+not+available+in+Vega+Calendar"} target={"_blank"}>Read more...</Link>
						</FormHelperText>
					</FormControl>
					<CapacityModifierControl
						value={capacityModifier}
						setValue={setCapacityModifier}
						hint={"Default capacity modifier. Can be overridden by events."}
					/>
					<FormControl variant={"standard"} fullWidth>
						<TypeaheadControl
							label={"Custom Tags"}
							free
							values={tags}
							onChange={(values) => setTags(values)}
							requestPolicy={"cache-and-network"}
							query={EmployeeTagsQuery}
						/>
						<FormHelperText>Use tags for custom grouping and categorization in calendars.</FormHelperText>
					</FormControl>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<LoadingButton
					loading={updateResult.fetching}
					onClick={() => {
						const data: Partial<Employee> = {
							startTime: convertTimeToString(startTime),
							endTime: convertTimeToString(endTime),
							gsacOooEnabled: Boolean(gsacOooEnabled),
							tags: tags.length > 0 ? tags : null,
							dateOfBirth: null,
							capacityModifier
						};
						if (dobEnabled) {
							data.dateOfBirth = new Date(Date.UTC(new Date().getFullYear(), dobMonth, dobDay));
						}
						updateEmployee({ id: employee.id, data }).then(response => {
							if (response.data) {
								onClose();
								toast.success("Profile settings updated successfully");
							}
						});
					}}
					variant={"contained"}
					color={"primary"}
				>
					Save
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);

}